import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { AjsResponse, wrapAjs } from '../ajs-response';
import { buildParams, PAGINATION_FIELDS } from '../http-params.util';

/**
 * AdministrationApiService (Angular 18) — tenant administration, email
 * templates, and language packs.
 *
 * Replaces apps/b2b-ui/common/services/api/administrationApi/administrationApi.service.js.
 *
 * Two sub-namespaces:
 *   apiService.administration.emailTemplates.{list,update,verify}
 *   apiService.administration.languages.{list,listCustom,update}
 *
 * Legacy quirks preserved:
 *   - `update()` whitelists a long payload shape and forces `allowAMEX: true`.
 *   - `update()` sets `amexPaymentProcessor` from `props.paymentProcessor`
 *     (legacy field aliasing).
 *   - `tenantConfigWithEmailPassword()` propagates `x-acme-email` /
 *     `x-acme-password` headers (used during password reset).
 *   - `emailTemplates.verify()` pre-stringifies its body with
 *     `JSON.stringify(props)` for parity with the legacy request (this does
 *     not double-encode — both legacy `$http` and `HttpClient` send a string
 *     body as-is). Because the body is a string rather than an object,
 *     `HttpClient` would otherwise infer `Content-Type: text/plain`, so the
 *     header is set explicitly.
 */
const ADMINISTRATION_BASE_V2 = '/v2/api/administration';
const EMAIL_TEMPLATES_BASE_V1 = '/v1/api/email/templates';
const EMAIL_TEMPLATES_BASE_V2 = '/v2/api/email/templates';
const LANGUAGES_BASE_V2 = '/v2/api/languages';

interface AdministrationUpdatePayload {
  id: number | string;
  name?: string;
  streetAddress1?: string;
  streetAddress2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  phoneNumber?: string;
  companyUrl?: string;
  logoImage?: unknown;
  timezone?: string;
  consumerPage?: string;
  abbreviatedName?: string;
  giftCertificates?: string;
  reviews?: string;
  coupons?: unknown;
  membership?: unknown;
  membershipTicketTypeSelection?: boolean;
  membershipSales?: unknown;
  membershipBenefitsExtension?: unknown;
  membershipVerificationLastName?: unknown;
  paymentProcessor?: string;
  surveyFrequency?: unknown;
  offlinePin?: string;
  allowPrintingForExpiredMemberships?: boolean;
  enableSendGridTracking?: boolean;
  enableCustomerInputOnPurchase?: boolean;
  externalSystemIntegration?: string;
  enableExternalNotifications?: boolean;
  allowUpgradeExternalMemberships?: boolean;
  invalidateExtraneousMembershipCards?: boolean;
  vouchersRequireOrganization?: boolean;
  enableGoogleAnalyticsTracking?: boolean;
  enableGtmTracking?: boolean;
  notificationPreference?: unknown;
  sendGridProductionKey?: string;
  sendGridSandboxKey?: string;
  disableMembershipManage?: boolean;
  rounding?: unknown;
  billingAddress?: unknown;
  b2bBillingAddress?: unknown;
  passwordSecurityPolicyEnabled?: boolean;
  passwordMinimumLength?: number;
  passwordUseMixedCase?: boolean;
  passwordMinimumDigits?: number;
  passwordMinimumSymbols?: number;
  sessionExpiryInMillis?: number;
  settings?: unknown;
  printers?: unknown;
  expressURL?: string;
  expressAccountID?: string;
  expressAccountToken?: string;
  expressApplicationID?: string;
  expressAcceptorID?: string;
  creditCardValidationSettings?: unknown;
  featuresConfig?: unknown;
  lockAlwaysRequiredToUpdateRequiresLockObjects?: boolean;
  lockExpiryInSeconds?: number;
  auxiliaryMembership?: unknown;
  sortTemplatesByDisplayOrder?: boolean;
  allowTicketTransactionOnExpiredMemberships?: boolean;
}

@Injectable({ providedIn: 'root' })
export class AdministrationApiService {
  private readonly http = inject(HttpClient);

  list(): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(ADMINISTRATION_BASE_V2));
  }

  // Legacy `detail(id)` ignored `id` and hit the base URL — preserved.
  detail(_id?: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(ADMINISTRATION_BASE_V2));
  }

  update(props: AdministrationUpdatePayload): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.put<unknown>(ADMINISTRATION_BASE_V2, {
        id: props.id,
        name: props.name,
        streetAddress1: props.streetAddress1,
        streetAddress2: props.streetAddress2,
        city: props.city,
        state: props.state,
        zipCode: props.zipCode,
        country: props.country,
        phoneNumber: props.phoneNumber,
        companyUrl: props.companyUrl,
        logoImage: props.logoImage,
        timezone: props.timezone,
        consumerPage: props.consumerPage,
        abbreviatedName: props.abbreviatedName,
        giftCertificates: props.giftCertificates,
        reviews: props.reviews,
        coupons: props.coupons,
        membership: props.membership,
        membershipTicketTypeSelection: props.membershipTicketTypeSelection,
        membershipSales: props.membershipSales,
        membershipBenefitsExtension: props.membershipBenefitsExtension,
        membershipVerificationLastName: props.membershipVerificationLastName,
        allowAMEX: true,
        amexPaymentProcessor: props.paymentProcessor,
        paymentProcessor: props.paymentProcessor,
        surveyFrequency: props.surveyFrequency,
        offlinePin: props.offlinePin,
        allowPrintingForExpiredMemberships: props.allowPrintingForExpiredMemberships,
        enableSendGridTracking: props.enableSendGridTracking,
        enableCustomerInputOnPurchase: props.enableCustomerInputOnPurchase,
        externalSystemIntegration: props.externalSystemIntegration,
        enableExternalNotifications: props.enableExternalNotifications,
        allowUpgradeExternalMemberships: props.allowUpgradeExternalMemberships,
        invalidateExtraneousMembershipCards: props.invalidateExtraneousMembershipCards,
        vouchersRequireOrganization: props.vouchersRequireOrganization,
        enableGoogleAnalyticsTracking: props.enableGoogleAnalyticsTracking,
        enableGtmTracking: props.enableGtmTracking,
        notificationPreference: props.notificationPreference,
        sendGridProductionKey: props.sendGridProductionKey,
        sendGridSandboxKey: props.sendGridSandboxKey,
        disableMembershipManage: props.disableMembershipManage,
        rounding: props.rounding,
        billingAddress: props.billingAddress,
        b2bBillingAddress: props.b2bBillingAddress,
        passwordSecurityPolicyEnabled: props.passwordSecurityPolicyEnabled,
        passwordMinimumLength: props.passwordMinimumLength,
        passwordUseMixedCase: props.passwordUseMixedCase,
        passwordMinimumDigits: props.passwordMinimumDigits,
        passwordMinimumSymbols: props.passwordMinimumSymbols,
        sessionExpiryInMillis: props.sessionExpiryInMillis,
        settings: props.settings,
        printers: props.printers,
        expressURL: props.expressURL,
        expressAccountID: props.expressAccountID,
        expressAccountToken: props.expressAccountToken,
        expressApplicationID: props.expressApplicationID,
        expressAcceptorID: props.expressAcceptorID,
        creditCardValidationSettings: props.creditCardValidationSettings,
        featuresConfig: props.featuresConfig,
        lockAlwaysRequiredToUpdateRequiresLockObjects: props.lockAlwaysRequiredToUpdateRequiresLockObjects,
        lockExpiryInSeconds: props.lockExpiryInSeconds,
        auxiliaryMembership: props.auxiliaryMembership,
        sortTemplatesByDisplayOrder: props.sortTemplatesByDisplayOrder,
        allowTicketTransactionOnExpiredMemberships: props.allowTicketTransactionOnExpiredMemberships,
      }),
    );
  }

  tenantConfig(token?: string): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(`${ADMINISTRATION_BASE_V2}/tenantconfig`, {
        params: token ? buildParams({ passwordRecoveryToken: token }) : undefined,
      }),
    );
  }

  tenantConfigWithEmailPassword(props: { email: string; password: string }): Promise<AjsResponse<unknown>> {
    const headers = new HttpHeaders({
      'x-acme-email': props.email,
      'x-acme-password': props.password,
    });
    return wrapAjs(
      this.http.get<unknown>(`${ADMINISTRATION_BASE_V2}/tenantconfig`, {
        headers,
      }),
    );
  }

  readonly emailTemplates = {
    list: (props?: Record<string, unknown>): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(EMAIL_TEMPLATES_BASE_V2, {
          params: buildParams(props, PAGINATION_FIELDS),
        }),
      ),

    update: (props: unknown): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.put<unknown>(`${EMAIL_TEMPLATES_BASE_V2}/batch`, props)),

    // Body is pre-stringified for parity with the legacy request (see class
    // doc above — this is single-, not double-, encoded). Because the body is
    // a string, HttpClient would otherwise infer Content-Type: text/plain
    // instead of legacy $http's default application/json.
    verify: (props: {
      sandboxApiKey: string;
      prodApiKey: string;
      templates: unknown[];
    }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.post<unknown>(`${EMAIL_TEMPLATES_BASE_V2}/verify`, JSON.stringify(props), {
          headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        }),
      ),
  };

  readonly languages = {
    list: (): Promise<AjsResponse<unknown>> => wrapAjs(this.http.get<unknown>(`${LANGUAGES_BASE_V2}/all`)),

    listCustom: (): Promise<AjsResponse<unknown>> => wrapAjs(this.http.get<unknown>(LANGUAGES_BASE_V2)),

    update: (props: unknown): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.put<unknown>(LANGUAGES_BASE_V2, props)),
  };
}
